import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type Embed,
  type EmbedDataResult,
  type ListEmbedsResult,
} from '@whinerp/client';
import {
  Button,
  EmptyState,
  Field,
  InlineAction,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { CaptureFormPanel } from './capture-form-panel.js';
import { ChatScriptPanel } from './chat-script-panel.js';

/**
 * Setup > Developer > Embeds (F-136).
 *
 * The ux_pointer is the whole reason the preview exists: "Preview inside the
 * config screen. Embedding is a trial-and-error task without one." So the
 * screen mints a real token against the embed and renders what the host would
 * see - including when what the host would see is nothing, which is the state
 * somebody otherwise spends an afternoon proving.
 */
export function EmbedsPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const client = useQueryClient();

  const [name, setName] = useState('');
  const [widget, setWidget] = useState('list');
  const [objectKey, setObjectKey] = useState('contact');
  const [origins, setOrigins] = useState('');
  const [previewing, setPreviewing] = useState<Embed | null>(null);

  const embeds = useQuery<ListEmbedsResult, ApiError>({
    queryKey: ['embeds'],
    queryFn: () => api.listEmbeds({}),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['embeds'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.createEmbed({
        body: {
          name,
          widget,
          objectKey: widget === 'pipeline' || widget === 'chat' ? null : objectKey,
          origins: origins.split('\n').map((line) => line.trim()).filter(Boolean),
        },
      }),
    onSuccess: () => {
      setName('');
      setOrigins('');
      refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: (embedId: string) => api.revokeEmbed({ path: { embedId } }),
    onSuccess: refresh,
  });

  /*
   * The preview mints a REAL token and reads through the public route.
   *
   * Not a local render of the same component with the administrator's own
   * data: that previews this screen's permissions, not the embed's, and would
   * show a pipeline to somebody whose embed will render an empty one.
   */
  const preview = useMutation<EmbedDataResult, ApiError, Embed>({
    mutationFn: async (embed) => {
      const minted = await api.previewEmbed({ path: { embedId: embed.embedId }, body: {} });
      return api.embedData({ query: { token: minted.token, limit: 5 } });
    },
    onSuccess: (_data, embed) => setPreviewing(embed),
  });

  // A skeleton means "still loading". Rendering one on error is a spinner that
  // never resolves.
  if (embeds.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (embeds.isError) return <></>;

  return (
    <Panel data-region="embeds" id="embeds">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('embed.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('embed.body')}</p>

        <div className="flex flex-col gap-2">
          <Field
            label={t('embed.name')}
            value={name}
            data-embed-name
            onChange={(event) => setName(event.target.value)}
          />
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('embed.widget')}
            <select
              data-embed-widget
              value={widget}
              onChange={(event) => setWidget(event.target.value)}
              className="min-h-touch rounded-chip border border-line bg-surface px-2 text-body text-ink"
            >
              {embeds.data.widgets.map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
          {/* A pipeline or a chat is not about one object, so it is not asked. */}
          {widget === 'pipeline' || widget === 'chat' ? null : (
            <Field
              label={t('embed.object')}
              value={objectKey}
              data-embed-object
              onChange={(event) => setObjectKey(event.target.value)}
            />
          )}
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('embed.origins')}
            <textarea
              data-embed-origins
              rows={3}
              value={origins}
              onChange={(event) => setOrigins(event.target.value)}
              className="rounded-chip border border-line bg-surface p-2 text-dense text-ink"
            />
            <span>{t('embed.originsHelp')}</span>
          </label>
          <div>
            <Button
              data-embed-create
              intent="primary"
              disabled={name.trim() === '' || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('embed.create')}
            </Button>
          </div>
          {create.isError ? (
            <p role="alert" data-embed-error className="text-meta text-danger">
              {errorMessage(create.error)}
            </p>
          ) : null}
        </div>

        {embeds.data.embeds.length === 0 ? (
          <EmptyState
            data-embed-empty
            illustration="Code"
            title={t('embed.title')}
            body={t('embed.empty')}
            primary={{ label: t('embed.create'), icon: 'Plus', href: '/setup#embeds' }}
          />
        ) : (
          <ul className="flex flex-col gap-2" data-embed-list>
            {embeds.data.embeds.map((embed) => (
              <li
                key={embed.embedId}
                data-embed={embed.embedId}
                data-embed-revoked={embed.revokedAt !== null}
                className="flex flex-col gap-1 rounded-chip bg-sunken px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-body text-ink">{embed.name}</span>
                  <span className="text-meta text-ink-meta">{embed.widget}</span>
                  <Num className="text-meta text-ink-meta">
                    {t('embed.tokenLife', {
                      values: { seconds: formatCount(embed.tokenSeconds, format) },
                    })}
                  </Num>
                  {embed.revokedAt === null ? (
                    <>
                      {/* A chat is tried in its own panel below, not previewed as records. */}
                      {embed.widget === 'chat' ? null : (
                        <InlineAction
                          data-embed-preview={embed.embedId}
                          onClick={() => preview.mutate(embed)}
                        >
                          {t('embed.preview')}
                        </InlineAction>
                      )}
                      <InlineAction
                        data-embed-revoke={embed.embedId}
                        onClick={() => revoke.mutate(embed.embedId)}
                      >
                        {t('embed.revoke')}
                      </InlineAction>
                    </>
                  ) : (
                    <span className="text-meta text-ink-meta">{t('embed.revoked')}</span>
                  )}
                </div>
                {/* Said plainly. An embed with no origins renders nowhere, and
                    a list that only showed an empty array would leave somebody
                    wondering why their site shows nothing. */}
                {embed.origins.length === 0 ? (
                  <span data-embed-no-origins className="text-meta text-warning-text">
                    {t('embed.noOrigins')}
                  </span>
                ) : (
                  <span className="text-meta text-ink-meta">{embed.origins.join(', ')}</span>
                )}
                {/* A capture form is configured where it lives, because the
                    form IS the embed: the origin allowlist and the token that
                    make it safe to put in somebody else's page are on this
                    row already. */}
                {embed.widget === 'form' && embed.revokedAt === null ? (
                  <CaptureFormPanel embedId={embed.embedId} />
                ) : null}
                {/* A chat's script is chosen - and tried - on its row (G-185). */}
                {embed.widget === 'chat' && embed.revokedAt === null ? (
                  <ChatScriptPanel embedId={embed.embedId} chatFlowId={embed.chatFlowId} />
                ) : null}
                <span className="text-meta text-ink-meta">{t('embed.snippet')}</span>
                <code
                  data-embed-snippet={embed.embedId}
                  className="select-all break-all rounded-chip bg-surface px-2 py-1 text-dense text-ink"
                >
                  {`<whinerp-widget embed="${embed.embedId}"></whinerp-widget>`}
                </code>
              </li>
            ))}
          </ul>
        )}

        {preview.isError ? (
          <p role="alert" data-embed-preview-failed className="text-meta text-danger">
            {/* Whatever is wrong here is what the host site would see, which is
                the entire point of previewing inside the config screen. */}
            {t('embed.previewFailed')} {errorMessage(preview.error)}
          </p>
        ) : null}
        {previewing && preview.data ? (
          <div className="flex flex-col gap-1" data-embed-preview-result>
            <SectionLabel>{previewing.name}</SectionLabel>
            {preview.data.records.length === 0 ? (
              <p data-embed-preview-empty className="text-meta text-ink-meta">
                {t('embed.previewEmpty')}
              </p>
            ) : (
              <ul className="flex flex-col">
                {preview.data.records.map((record, index) => (
                  <li key={index} className="text-meta text-ink">
                    {String(record['name'] ?? record['full_name'] ?? record['first_name'] ?? '')}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
